import * as signalR from "@microsoft/signalr";

class GenericSignalRService {
  static instances = {};

  constructor(hubUrl) {
    if (GenericSignalRService.instances[hubUrl]) {
      return GenericSignalRService.instances[hubUrl];
    }

    this.hubUrl = hubUrl;
    this.connection = null;
    this.subscribers = {};
    this.isConnecting = false;
    this.joinedGroups = new Set();

    GenericSignalRService.instances[hubUrl] = this;
  }

  subscribe(eventName, callback) {
    if (!this.subscribers[eventName]) {
      this.subscribers[eventName] = new Set();
    }
    this.subscribers[eventName].add(callback);
    return () => this.subscribers[eventName].delete(callback);
  }

  _notify(eventName, data) {
    if (this.subscribers[eventName]) {
      for (const cb of this.subscribers[eventName]) {
        try {
          cb(data);
        } catch (e) {
          console.error(`Error in ${eventName} subscriber:`, e);
        }
      }
    }
  }

  async startConnection() {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      console.warn(`Already connected to ${this.hubUrl}`);
      return;
    }

    if (this.isConnecting) {
      console.warn(`Already attempting connection to ${this.hubUrl}`);
      await this._waitForConnectionState(signalR.HubConnectionState.Connected, 3000);
      return;
    }

    this.isConnecting = true;

    // Save groups that were joined on the previous connection so we can re-join
    // them after the new connection is established. Clear the set now so that
    // joinGroup() doesn't skip them when called below (the old "already joined"
    // guard would silently no-op otherwise, leaving the new connection groupless).
    const groupsToRejoin = Array.from(this.joinedGroups);
    this.joinedGroups.clear();

    // Dispose existing connection if any.
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (e) {
        console.error("Error stopping previous connection:", e);
      }
      this.connection = null;
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, { withCredentials: true })
      .withAutomaticReconnect([1000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.connection.onclose((error) => {
      console.warn(`Disconnected from ${this.hubUrl}`, error);
      this._notify("Error", error);
      // Auto-reconnect has exhausted all retries. Schedule a fresh startConnection
      // so the connection (and its group memberships) are restored when the tab is active.
      setTimeout(() => {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
          this.startConnection().catch((e) =>
            console.error(`Scheduled reconnect failed for ${this.hubUrl}:`, e)
          );
        }
      }, 10000);
    });

    this.connection.onreconnecting((error) => {
      console.log(`Reconnecting to ${this.hubUrl}`, error);
    });

    this.connection.onreconnected(async (connectionId) => {
      console.log(`Reconnected to ${this.hubUrl} (${connectionId})`);
      // On reconnection
      const groupsToRejoin = Array.from(this.joinedGroups);
      // Clear the local 
      this.joinedGroups.clear();
      for (const groupName of groupsToRejoin) {
        try {
          await this.joinGroup(groupName);
        } catch (e) {
          console.error(`Failed to rejoin group ${groupName} after reconnection:`, e);
        }
      }
    });

    // Setup event listeners.
    this.connection.on("Error", (errMessage) => this._notify("Error", errMessage));
    this.connection.on("UserJoined", (userId) => this._notify("UserJoined", userId));
    this.connection.on("UserLeft", (userId) => this._notify("UserLeft", userId));
    this.connection.on("UpdateDesigns", (data) => this._notify("UpdateDesigns", data));
    this.connection.on("UpdateSMSDesigns", (data) => this._notify("UpdateSMSDesigns", data));
    this.connection.on("UpdateProcessingStatus", (data) => this._notify("UpdateProcessingStatus", data));

    try {
      await this.connection.start();
      console.log(`Connected successfully to ${this.hubUrl}`);
      // Wait for connection
      await this._waitForConnectionState(signalR.HubConnectionState.Connected, 3000);
      // Re-join any groups that existed on the previous connection.
      // Mirrors the onreconnected handler — ensures group membership survives
      // a manual startConnection call (e.g. after auto-reconnect gives up).
      for (const groupName of groupsToRejoin) {
        try {
          await this.joinGroup(groupName);
        } catch (e) {
          console.error(`Failed to rejoin group ${groupName} after startConnection:`, e);
        }
      }
    } catch (error) {
      console.error(`Connection start error to ${this.hubUrl}:`, error);
      this._notify("Error", error);
    } finally {
      this.isConnecting = false;
    }
  }

  // Waits until 
  async _waitForConnectionState(desiredState, timeout = 3000) {
    const start = Date.now();
    let lastLogTime = 0;
    while (this.connection.state !== desiredState) {
      const elapsed = Date.now() - start;
      if (elapsed > timeout) {
        throw new Error(`Timed out waiting for connection to reach state ${desiredState}. Current state: ${this.connection.state}`);
      }
      if (elapsed - lastLogTime >= 1000) {
        console.log(`Waiting for connection state ${desiredState} (current state: ${this.connection.state}, elapsed: ${elapsed}ms)`);
        lastLogTime = elapsed;
      }
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  async joinGroup(groupName) {
    try {
      await this.ensureConnected();
    } catch (error) {
      console.error("Error ensuring connection:", error);
      this._notify("Error", error);
      return;
    }

    if (this.joinedGroups.has(groupName)) {
      console.log(`Already joined group ${groupName}, skipping.`);
      return;
    }

    try {
      await this.connection.invoke("JoinGroup", groupName.toString());
      this.joinedGroups.add(groupName);
      console.log(`Joined group ${groupName}`);
    } catch (err) {
      if (err.statusCode === 409) {
        console.warn(`Already joined group ${groupName} on server.`);
        this.joinedGroups.add(groupName); 
      } else if (err.statusCode === 429) {
        console.warn(`Rate limit hit while joining group ${groupName}, retrying later.`);
        setTimeout(() => this.joinGroup(groupName), 5000);
      } else {
        console.error(`Error joining group ${groupName}:`, err);
        this._notify("Error", err);
      }
    }
  }

  async leaveGroup(groupName) {
    await this.ensureConnected();

    if (!this.joinedGroups.has(groupName)) {
      console.log(`Not in group ${groupName}, skipping leave.`);
      return;
    }

    try {
      await this.connection.invoke("LeaveGroup", groupName.toString());
      this.joinedGroups.delete(groupName);
      console.log(`Left group ${groupName}`);
    } catch (err) {
      console.error(`Error leaving group ${groupName}:`, err);
      this._notify("Error", err);
    }
  }

  async ensureConnected() {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      await this.startConnection();
    }
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      await this._waitForConnectionState(signalR.HubConnectionState.Connected, 3000);
    }
  }

   // dispose the hub connection
  async dispose() {
    for (const groupName of Array.from(this.joinedGroups)) {
      await this.leaveGroup(groupName);
    }
    this.joinedGroups.clear();

    if (this.connection) {
      try {
        await this.connection.stop();
        console.log(`Disconnected from ${this.hubUrl}`);
      } catch (err) {
        console.error("Error stopping connection:", err);
      }
    }
  }
}

export default GenericSignalRService;
